import removedUser from 'Assets/images/removed-user.svg';
import cx from 'classnames';
import { observer } from 'mobx-react';
import { ContactModelV2 } from 'Models/ContactModelV2';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { useUIStore } from 'Modules/ui/index.store';
import { EntityType } from 'Modules/ui/sidePanel/store/index.types';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { List, ListItemProps, Loader, Popup } from 'semantic-ui-react';
import { getCurrentConversationId } from 'Utils/getCurrentConversationId';
import { resolveConversationPath } from 'Utils/routeNav';
import ActivityListItem from '../ActivityListItem';
import { UserAvatar } from '../shared/UserAvatar';
import { ChannelMemberItemProps } from './interfaces';

/**
 * List item display of a `Participant` in a `Channel`.
 *
 * **This named class is not decorated with `@observer`.** Use the default export from this module if you need the decorated class.
 *
 * @export
 * @class ChannelMembers
 */

export function ChannelMemberItem({
  getPerson,
  makeCall,
  participant,
  contact,
  uiStore,
  from,
  loggedInPersonId,
  conversationId,
  conversation,
  person,
  participantStore,
}: ChannelMemberItemProps): JSX.Element {
  const [hovered, setHovered] = React.useState<boolean>(false);

  // Keep optOutPhoneNumbersInfo here so the component updates when it changes
  const { getOptOutPhoneNumberInfo } = useConversationStore();

  const location = useLocation();
  const navigate = useNavigate();

  const onMouseOver = () => setHovered(true);
  const onMouseOut = () => setHovered(false);

  const _renderDefaultAvatar = () => (
    <div className="list-item-ih-image semantic-flex-zero">
      <div className="removed-user">
        R U
        <img src={removedUser} className="presence-badge" />
      </div>
    </div>
  );

  const openInfoDetails = (
    id: number | string,
    type: EntityType,
    from: 'personInfo' | 'groupInfo'
  ) => {
    if (from === 'personInfo') {
      useUIStore.getState().setEntitySidePanelInfo({
        entityId: id.toString(),
        entityType: type,
      });
      conversation.setChannelInfoDetails('0', 'new');
    } else {
      conversation.setChannelInfoDetails(id.toString(), type);
      useUIStore.getState().resetEntitySidePanel();
    }
  };

  const openChat = (personId) => () => {
    conversation
      .loadOrCreateConversationWithPost(personId)
      .then((resp) => navigate(`/chat/conversations/${resp.data.id}/menu`));
  };

  const openChatWithNumber = (phone) => () => {
    conversation
      .loadOrCreateConversationWithPost(0, phone)
      .then((resp) => navigate(`/chat/conversations/${resp.data.id}/menu`));
  };

  const updateRoutes = (path) => {
    const newPath = resolveConversationPath(location.pathname, path);
    navigate(newPath);
  };

  const personPbo = participant.personId
    ? getPerson(participant.personId)
    : null;

  const contactsPbo =
    participant.personId === 0 || !participant.personId
      ? person.getAllContactsWithSameNum(participant.phone)
      : null;

  const listItemProps: ListItemProps = {
    // stretched: true, (TODO: Prop seems to no longer apply? RP 2019-08-29)
    className: cx('list-item-ih semantic-flex-row channel-member-item', {
      faded: false,
      hovered,
    }),
  };

  if (
    (personPbo && personPbo.state === 'fulfilled') ||
    (contactsPbo && contactsPbo.state === 'fulfilled')
  ) {
    listItemProps.onMouseOver = onMouseOver;
    listItemProps.onMouseOut = onMouseOut;
    listItemProps.style = { cursor: 'pointer' };
  }

  let externalContact: ContactModelV2 | null = null;
  if (contactsPbo?.state === 'fulfilled') {
    externalContact = contactsPbo.case({
      fulfilled: (contacts) => contacts[0],
    });
  }

  return (
    <List.Item {...listItemProps}>
      {personPbo ? (
        personPbo.case({
          pending: () => <Loader active indeterminate size="mini" />,
          rejected: () => (
            <>
              {_renderDefaultAvatar()}
              <List.Content className="list-item-ih-content semantic-flex-fluid">
                <span className="color-removed">
                  Removed User #{participant.personId}
                </span>
              </List.Content>
            </>
          ),
          fulfilled: (personResp) => {
            const presence = uiStore.selectPersonPresenceStatus(
              personResp?.sourceId
            );
            return (
              <>
                <div className="list-item-ih-image semantic-flex-grow">
                  {
                    // After portal side make profile pic work, uncomment this one
                    // personResp.data.DisplayAvatar ?
                    // <Image className='profile-pic' src={`${personResp.data.DisplayAvatar}`} size='mini' />
                    // :
                    <UserAvatar
                      name={personResp.name}
                      presence={presence}
                      key={`channel-member-avatar-${personResp?.sourceId}-${presence?.state}`}
                    />
                  }
                </div>
                <List.Content
                  className="list-item-ih-content semantic-flex-fluid status-group-info"
                  id="group-members"
                  onClick={openChat(personResp.sourceId)}
                >
                  <div className="group-members-info-container">
                    <p data-private className="group-members-name">
                      {personResp.name}
                    </p>
                    {personResp.phoneNumber &&
                      getOptOutPhoneNumberInfo(personResp?.phoneNumber) ===
                        true && (
                        <p className="group-members-opted-out">Opted-out</p>
                      )}
                  </div>
                  <Popup
                    inverted
                    trigger={
                      <span className="message-status">
                        {uiStore.selectPersonMessageStatus(personResp.sourceId)
                          ?.title || ''}
                      </span>
                    }
                    content={
                      uiStore.selectPersonMessageStatus(personResp.sourceId)
                        ?.message || ''
                    }
                    position={'top right'}
                  />
                </List.Content>
                <div className={cx('list-item-ih-hover-buttons', { hovered })}>
                  {from === 'group-info' ? (
                    <ActivityListItem
                      conversation={conversation?.CurrentConversation}
                      conversationId={conversation ? conversationId : '0'}
                      isCurrentConversation={false}
                      loggedInAccountId={person.loggedInAccountId}
                      loggedInPersonId={loggedInPersonId}
                      makeCall={makeCall}
                      key={conversationId}
                      loadPresenceIfMissing={uiStore.loadPresenceIfMissing}
                      loggedInUserActiveConferenceConversation={
                        conversation.LoggedInUserActiveConferenceConversation
                      }
                      participant={participantStore}
                      participantsPbo={participantStore.selectParticipantsByConversationId(
                        getCurrentConversationId()
                      )}
                      postConferenceByConversationId={
                        conversation.postConferenceByConversationId
                      }
                      selectParticipantPersonInfo={
                        person.loadPersonByIdGetIfMissingGet
                      }
                      selectPersonPresenceStatus={
                        uiStore.selectPersonPresenceStatus
                      }
                      updateRoute={updateRoutes}
                      selectUnreadCounts={
                        uiStore.selectConversationUnreadCounts
                      }
                      setConversationAndTotalUnreadCount={
                        uiStore.setConversationAndTotalUnreadCount
                      }
                      updateMyLastReadMessage={
                        participantStore.updateMyLastReadMessage
                      }
                      personStore={person}
                      conversationStore={conversation}
                      from={'group-info'}
                      handleDetails={openInfoDetails}
                      currentPersonId={personResp.sourceId}
                      isPerson
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </>
            );
          },
        })
      ) : contactsPbo && contactsPbo.case ? (
        contactsPbo.case({
          pending: () => <Loader active indeterminate size="mini" />,
          rejected: () => (
            <>
              {_renderDefaultAvatar()}
              <List.Content className="list-item-ih-content semantic-flex-fluid">
                <span className="color-removed">
                  Removed User #{participant.phone}
                </span>
              </List.Content>
            </>
          ),
          fulfilled: (contacts) => {
            const contactBase = conversation.createParticipantForNumberGroup(
              participant.phone
            );

            const contact = contacts[0];

            const phoneNumber =
              contact?.phoneNumber ?? contactBase?.phoneNumber;
            const name = contact?.name ?? contactBase?.DisplayName;

            return (
              <>
                <div className="list-item-ih-image semantic-flex-grow">
                  {
                    // After portal side make profile pic work, uncomment this one
                    // personResp.data.DisplayAvatar ?
                    // <Image className='profile-pic' src={`${personResp.data.DisplayAvatar}`} size='mini' />
                    // :
                    <UserAvatar name={null} hideInitials />
                  }
                </div>
                <List.Content
                  className="list-item-ih-content semantic-flex-fluid status-group-info"
                  id="group-members"
                  onClick={openChatWithNumber(phoneNumber)}
                >
                  <div className="group-members-info-container">
                    <p data-private className="group-members-name">
                      {externalContact?.name || name}
                    </p>
                    {phoneNumber &&
                      getOptOutPhoneNumberInfo(
                        phoneNumber ||
                          externalContact?.phoneNumbers?.[0]?.number
                      ) === true && (
                        <p className="group-members-opted-out">Opted-out</p>
                      )}
                  </div>
                </List.Content>
                <Popup
                  inverted
                  trigger={
                    <span className="message-status">
                      {uiStore.selectPersonMessageStatus(phoneNumber)?.title ||
                        ''}
                    </span>
                  }
                  content={
                    uiStore.selectPersonMessageStatus(phoneNumber)?.message ||
                    ''
                  }
                  position={'top right'}
                />
                <div className={cx('list-item-ih-hover-buttons', { hovered })}>
                  {from === 'group-info' ? (
                    <ActivityListItem
                      conversation={conversation?.CurrentConversation}
                      conversationId={conversation ? conversationId : '0'}
                      isCurrentConversation={false}
                      loggedInAccountId={person.loggedInAccountId}
                      loggedInPersonId={loggedInPersonId}
                      makeCall={makeCall}
                      key={conversationId}
                      loadPresenceIfMissing={uiStore.loadPresenceIfMissing}
                      loggedInUserActiveConferenceConversation={
                        conversation.LoggedInUserActiveConferenceConversation
                      }
                      participant={participantStore}
                      participantsPbo={participantStore.selectParticipantsByConversationId(
                        getCurrentConversationId()
                      )}
                      postConferenceByConversationId={
                        conversation.postConferenceByConversationId
                      }
                      selectParticipantPersonInfo={
                        person.loadPersonByIdGetIfMissingGet
                      }
                      selectPersonPresenceStatus={
                        uiStore.selectPersonPresenceStatus
                      }
                      updateRoute={updateRoutes}
                      selectUnreadCounts={
                        uiStore.selectConversationUnreadCounts
                      }
                      setConversationAndTotalUnreadCount={
                        uiStore.setConversationAndTotalUnreadCount
                      }
                      updateMyLastReadMessage={
                        participantStore.updateMyLastReadMessage
                      }
                      personStore={person}
                      conversationStore={conversation}
                      from={'group-info'}
                      handleDetails={openInfoDetails}
                      currentPersonId={phoneNumber}
                      isPerson={false}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </>
            );
          },
        })
      ) : (
        <span className="person-pbo-missing">...</span>
      )}
    </List.Item>
  );
}

export default observer(ChannelMemberItem);
